import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const TOKEN_KEY      = 'hm_admin_token';
const USER_TOKEN_KEY = 'hm_user_token';
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user,        setUser]        = useState(null);   // admin session
    const [userSession, setUserSession] = useState(null);   // customer Google session
    const [loading, setLoading] = useState(true); // true while verifying stored session on mount

    // On mount: verify any stored session token with the server.
    // This restores the session after a page refresh without a full Google re-login.
    useEffect(() => {
        // Clean up legacy auth_user key from the old (pre-session) auth system
        localStorage.removeItem('auth_user');

        const adminToken = localStorage.getItem(TOKEN_KEY);
        const userToken  = localStorage.getItem(USER_TOKEN_KEY);

        const checks = [];

        if (adminToken) {
            checks.push(
                fetch('/api/admin/auth/me', { headers: { 'x-admin-token': adminToken } })
                    .then(r => r.json())
                    .then(d => { if (d.ok) setUser(d.profile); else localStorage.removeItem(TOKEN_KEY); })
                    .catch(() => {})
            );
        }

        if (userToken) {
            checks.push(
                fetch('/api/user-auth/me', { headers: { 'x-user-token': userToken } })
                    .then(r => r.json())
                    .then(d => { if (d.ok) setUserSession(d.profile); else localStorage.removeItem(USER_TOKEN_KEY); })
                    .catch(() => {})
            );
        }

        Promise.all(checks).finally(() => setLoading(false));

        // Re-validate the admin session every 30 minutes while the page is open.
        const revalidateInterval = setInterval(() => {
            const t = localStorage.getItem(TOKEN_KEY);
            if (!t) return;
            fetch('/api/admin/auth/me', { headers: { 'x-admin-token': t } })
                .then(r => r.json())
                .then(d => {
                    if (!d.ok) {
                        localStorage.removeItem(TOKEN_KEY);
                        setUser(null);
                    }
                })
                .catch(() => {});
        }, 30 * 60 * 1000);

        return () => clearInterval(revalidateInterval);
    }, []);

    /**
     * Sign in via Google OAuth.
     * Sends the Google access-token to the server which verifies the email
     * against the site's allowed-emails list, then issues a 24-hour session token.
     * Returns { ok: true } or { ok: false, error: string }.
     */
    const signIn = useCallback(async (googleAccessToken) => {
        try {
            const res  = await fetch('/api/admin/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: googleAccessToken }),
            });
            const data = await res.json();
            if (!data.ok) return { ok: false, error: data.error || 'Login failed' };
            localStorage.setItem(TOKEN_KEY, data.token);
            setUser(data.profile);
            return { ok: true };
        } catch {
            return { ok: false, error: 'Network error \u2014 is the server running?' };
        }
    }, []);

    /**
     * Unified sign-in: tries admin auth first.
     * If authorized as admin → sets admin session, returns { ok: true, isAdmin: true }.
     * If NOT an admin (403) → falls back to regular customer session, returns { ok: true, isAdmin: false }.
     * Server errors are returned as-is without fallback.
     */
    const unifiedSignIn = useCallback(async (googleAccessToken) => {
        try {
            const adminRes = await fetch('/api/admin/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: googleAccessToken }),
            });
            const adminData = await adminRes.json();
            if (adminData.ok) {
                localStorage.setItem(TOKEN_KEY, adminData.token);
                setUser(adminData.profile);
                return { ok: true, isAdmin: true };
            }
            // Only fall back to user auth when the email is simply not on the admin list (403)
            if (adminRes.status !== 403) {
                return { ok: false, error: adminData.error || 'Authentication failed' };
            }
            // Not an admin — try regular user login
            const userRes = await fetch('/api/user-auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: googleAccessToken }),
            });
            const userData = await userRes.json();
            if (!userData.ok) return { ok: false, error: userData.error || 'Login failed' };
            localStorage.setItem(USER_TOKEN_KEY, userData.token);
            setUserSession(userData.profile);
            return { ok: true, isAdmin: false };
        } catch {
            return { ok: false, error: 'Network error \u2014 is the server running?' };
        }
    }, []);

    // signInWithCode (access-code login) was removed in the 2026-08 hardening \u2014
    // the server no longer accepts static admin tokens; Google sign-in only.

    /** Sign out: invalidate session on the server and clear local state. */
    const signOut = useCallback(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            fetch('/api/admin/auth/logout', {
                method: 'POST',
                headers: { 'x-admin-token': token },
            }).catch(() => {});
        }
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        window.dispatchEvent(new Event('hm-admin-signout'));
    }, []);

    /**
     * Sign in as a regular customer via Google OAuth.
     * Accepts ANY verified Google account — no allowlist restriction.
     * Returns { ok: true } or { ok: false, error: string }.
     */
    const userSignIn = useCallback(async (googleAccessToken) => {
        try {
            const res  = await fetch('/api/user-auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: googleAccessToken }),
            });
            const data = await res.json();
            if (!data.ok) return { ok: false, error: data.error || 'Login failed' };
            localStorage.setItem(USER_TOKEN_KEY, data.token);
            setUserSession(data.profile);
            return { ok: true };
        } catch {
            return { ok: false, error: 'Network error \u2014 is the server running?' };
        }
    }, []);

    /** Sign out customer session. */
    const userSignOut = useCallback(() => {
        const token = localStorage.getItem(USER_TOKEN_KEY);
        if (token) {
            fetch('/api/user-auth/logout', {
                method: 'POST',
                headers: { 'x-user-token': token },
            }).catch(() => {});
        }
        localStorage.removeItem(USER_TOKEN_KEY);
        setUserSession(null);
    }, []);
    // Memoised: App re-renders on every navigation, and a fresh object here
    // re-rendered every consumer (memoised cards included) each time.
    const authValue = useMemo(() => ({ user, userSession, loading, signIn, signOut, userSignIn, userSignOut, unifiedSignIn }),
        [user, userSession, loading, signIn, signOut, userSignIn, userSignOut, unifiedSignIn]);

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
